import { Box, Button, Stack, TextField } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { useEffect, useRef, type FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseObjectFields, AmountFields } from './shared/ObjectFieldComponents';

interface ElectronicObject {
    id: string;
    enabled: boolean;
    denumire: string;
    cantitate: number;
    um: string;
    pretUnitar: number;
    sumainLei: number;
    sumainEuro: number;
    tip: string;
}

interface ElectronicObjectRowProps {
    obj: ElectronicObject;
    originalIndex: number;
    onObjectChange: (index: number, field: keyof ElectronicObject, value: unknown) => void;
    onOpenElectronicsModal?: (index: number) => void;
    /** Keeps the values as quantity times unit price (session verification). */
    lockAmounts?: boolean;
}

const ElectronicObjectRow = ({
    obj,
    originalIndex,
    onObjectChange,
    onOpenElectronicsModal,
    lockAmounts = false,
}: ElectronicObjectRowProps) => {
    const { t } = useTranslation();
    const quantityRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (obj.enabled) {
            quantityRef.current?.focus();
        }
    }, [obj.enabled]);

    const handleFieldChange = (field: string, value: unknown) => {
        onObjectChange(originalIndex, field as keyof ElectronicObject, value);
    };

    // Show electronics modal button for electronics objects (not for "Altele")
    const showElectronicsButton =
        obj.enabled && obj.tip === 'Electronice' && onOpenElectronicsModal;

    return (
        <Box key={obj.id} mb={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <BaseObjectFields
                    enabled={obj.enabled}
                    denumire={obj.denumire}
                    onFieldChange={handleFieldChange}
                />
                {showElectronicsButton && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Settings />}
                        onClick={() => onOpenElectronicsModal(originalIndex)}
                        sx={{ minWidth: 100 }}
                    >
                        {t('electronics.configure', 'Configure')}
                    </Button>
                )}
                <TextField
                    size="small"
                    label={t('contracts.quantity')}
                    type="number"
                    value={obj.cantitate === 0 ? '' : obj.cantitate}
                    onChange={e =>
                        onObjectChange(originalIndex, 'cantitate', Number(e.target.value))
                    }
                    disabled={!obj.enabled}
                    sx={{ width: 80 }}
                    slotProps={{ htmlInput: { ref: quantityRef, onFocus: (e: FocusEvent<HTMLInputElement>) => e.target.select() } }}
                />
                <TextField
                    size="small"
                    label={t('contracts.um')}
                    value={obj.um}
                    onChange={e => onObjectChange(originalIndex, 'um', e.target.value)}
                    disabled={!obj.enabled}
                    sx={{ width: 60 }}
                />
                <AmountFields
                    enabled={obj.enabled}
                    pretUnitar={obj.pretUnitar}
                    sumainLei={obj.sumainLei}
                    sumainEuro={obj.sumainEuro}
                    onFieldChange={handleFieldChange}
                    lockAmounts={lockAmounts}
                />
            </Stack>
        </Box>
    );
};

export default ElectronicObjectRow;
